import ChartConstants from '@Constants/ChartConstants';
import assignIn from 'lodash/assignIn';
import DOMPurify from 'dompurify';
import every from 'lodash/every';
import last from 'lodash/last';
import get from 'lodash/get';

export const DRUGLIKENESS_COMPONENTS = [
  {
    title: 'Lipinski rules component',
    children: [
      'lipinskiComponent.molecularWeight',
      'lipinskiComponent.logP',
      'lipinskiComponent.hba',
      'lipinskiComponent.hbd',
      'lipinskiComponent.matchingRules',
    ],
  },
  {
    title: 'Veber rules component',
    children: ['veberComponent.psa', 'veberComponent.boundCount', 'veberComponent.matchingRules'],
  },
];
// List of graph values
export const DRUGLIKENESS_GRAPH = [
  'lipinskiComponent.molecularWeight',
  'lipinskiComponent.hba',
  'lipinskiComponent.hbd',
  'veberComponent.psa',
  'lipinskiComponent.logP',
  'veberComponent.boundCount',
];
// Custom translations
export const DRUGLIKENESS_COMPONENT_LABELS = {
  table: {
    'lipinskiComponent.logP': 'cLogP',
    'lipinskiComponent.matchingRules': 'Matching Lipinski Rules',
    'lipinskiComponent.molecularWeight': 'Molecular Weight',
    'veberComponent.boundCount': 'Rotatable Bond (RotB)',
    'veberComponent.matchingRules': 'Matching Veber Rules',
    'veberComponent.psa': 'Polar Surface Area (PSA)',
  },
  graph: {
    'lipinskiComponent.logP': 'cLogP',
    'lipinskiComponent.molecularWeight': 'MW/100',
    'veberComponent.boundCount': 'RotB',
    'veberComponent.psa': 'PSA/10',
  },
};

export const LIPINSKI_VEBER_VALUES = [5, 10, 5, 14, 5, 10];

class DruglikenessComponent {
  static factories = []; // Other class decorators

  static TABLE_LABELS = DRUGLIKENESS_COMPONENT_LABELS.table; // Labels for tables

  static GRAPH_LABELS = DRUGLIKENESS_COMPONENT_LABELS.graph; // Labels for graph axis

  static TABLES = DRUGLIKENESS_COMPONENTS; // Table dataset

  static GRAPH = DRUGLIKENESS_GRAPH; // Graph dataset

  constructor(response, databaseId) {
    this.response = response;
    this.databaseId = databaseId;

    this.tableData = this.getTableData(); // tables

    const graphData = this.getGraphSeries();
    if (this.isValidGraphData(graphData)) {
      // graph data is correct
      this.graph = {
        labels: this.getGraphLabels(), // graph labels
        data: graphData,
      };
    }
  }

  isValidGraphData = (graphData) => {
    const size = get(graphData, '0.data.length');
    // if (count of values is equal) and (no empty values)
    return !!(
      graphData &&
      graphData.length > 1 &&
      every(graphData, ({ data }) => data && data.length && data.length === size && data.indexOf() === -1)
    );
  };

  // optimize all namespaces into single object
  getDataset() {
    if (!this._dataset) {
      this._dataset = {};
      const merge = (name) => {
        const object = this.response && this.response[name] ? this.response[name] : {};
        Object.keys(object).forEach((key) => {
          // id will be "componentName.ID"
          const id = `${name}.${key}`;
          this._dataset[id] = { id, value: object[key] };
        });
        return merge;
      };
      merge('lipinskiComponent')('veberComponent')('otherComponents');
    }
    return this._dataset;
  }

  getTableData() {
    const dataset = this.getDataset();
    // extract values for tables
    return DruglikenessComponent.TABLES.map(({ title, children }) => {
      const childs = [];
      children.forEach((id) => {
        const item = dataset[id];
        if (item) {
          childs.push({ id, title: this.getTableLabel(id), value: item.value });
        }
      });
      return { title, children: childs };
    }).filter(({ children }) => !!children.length);
  }

  getGraphLabels() {
    return DruglikenessComponent.GRAPH.map((id) => this.getGraphLabel(id)); // Axis labels
  }

  getGraphSeries() {
    const dataset = this.getDataset();
    return [
      assignIn(
        {
          data: DruglikenessComponent.GRAPH.map((id) => {
            const definition = dataset[id];
            if (definition) {
              const label = this.getGraphLabel(id);
              // const value = definition.value.replace(/<(?:.|\n)*?>/gm, ''); // remove eny html tag like hi
              const value = DOMPurify.sanitize(definition.value);
              // if label contain "/", then we divide value
              return Number((value / (label.split('/')[1] || 1)).toFixed(4));
            }
            return undefined;
          }),
        },
        ChartConstants.druglikenessChart.components.substance
      ),
      assignIn(
        {
          data: LIPINSKI_VEBER_VALUES, // static values
        },
        ChartConstants.druglikenessChart.components.rules
      ),
    ];
  }

  getTableLabel = (id) => this.constructor.TABLE_LABELS[id] || this.getSimpleLabel(id);

  getGraphLabel = (id) => this.constructor.GRAPH_LABELS[id] || this.getSimpleLabel(id);

  getSimpleLabel = (id) => last(id.split('.')).toUpperCase(); // use only original ID. without component name
}

export default DruglikenessComponent;
